import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { GetAccess, GetRole, CheckEditAction, CheckDeleteAction } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import NuAmount from "../../../../Components/Utils/NuAmount";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import { Divider } from "../Payables/PayablesTable";

// Payment status badge color themes helper
const getPaymentStatusTheme = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s.includes("paid") || s.includes("received") || s.includes("cleared") || s.includes("success") || s.includes("approved")) {
    return "green";
  }
  if (s.includes("overdue") || s.includes("cancel") || s.includes("reject") || s.includes("fail")) {
    return "red";
  }
  if (s.includes("partial")) {
    return "blue";
  }
  return "yellow";
};

const renderPaymentStatus = (status) => {
  const theme = getPaymentStatusTheme(status);

  const themes = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm",
    blue: "bg-blue-50 text-blue-700 border-blue-200/50 shadow-sm",
    yellow: "bg-amber-50 text-amber-700 border-amber-200/50 shadow-sm",
    red: "bg-rose-50 text-rose-700 border-rose-200/50 shadow-sm",
  };

  const colorClass = themes[theme] || themes.yellow;

  return (
    <span className={`inline-flex items-center text-center justify-center px-3.5 py-1 rounded-full text-[11px] font-bold border leading-snug tracking-wide max-w-[170px] ${colorClass}`}>
      {status || "N/A"}
    </span>
  );
};

// Days Elapsed Renderer
const renderDaysElapsed = (days) => {
  if (days === undefined || days === null) return <span className="text-gray-400">-</span>;
  return (
    <div className="flex items-center gap-1.5 text-gray-700 text-xs font-semibold">
      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>{days} {days === 1 ? "day" : "days"}</span>
    </div>
  );
};

// Receivables Table Row Component
const ReceivablesTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("receivables_edit") || GetRole();
  const canDelete = GetAccess("receivables_delete") || GetRole();

  const bgClass = "bg-white";
  const hoverClass = "hover:bg-white/80";

  const handleConfirmDelete = () => {
    handleDeleteData(`/receivable/${row.receivableId}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className={`${bgClass} ${hoverClass} transition-colors align-middle`}>
        {/* Checkbox */}
        <td className="p-4 text-center align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onSelect}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4 transition duration-150 ease-in-out cursor-pointer"
          />
        </td>

        {/* Invoice Out */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/payments/receivables/view/${row.receivableId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.invoiceRef || "N/A"}
            </Link>
            <CopyButton text={row.invoiceRef} />
          </div>
          {row.receivedFromName && (
            <div className="text-[11px] text-gray-500 font-semibold mt-0.5">
              From: {row.receivedFromName}
            </div>
          )}
        </td>

        {/* Category */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
            {row.category || "N/A"}
          </span>
          {row.accountType && (
            <div className="text-[10px] text-gray-400 font-semibold mt-1">
              Acct: {row.accountType}
            </div>
          )}
        </td>

        {/* Amount */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-gray-900 font-semibold text-xs">
            <NuAmount currency={row.vendorCurrency || row.currency} value={row.amount || 0} />
          </span>
        </td>

        {/* Recvd Amount */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex flex-col text-xs font-semibold">
            <span className="text-green-600 flex items-center gap-1 text-[13px]">
              <NuAmount currency={row.vendorCurrency || row.currency} value={row.receivedAmount || 0} />
            </span>
            {row.balanceAmount !== undefined && row.balanceAmount !== null && (
              <span className="text-gray-500 font-normal mt-0.5 text-[10px] flex items-center gap-0.5">
                Bal: <NuAmount currency={row.vendorCurrency || row.currency} value={row.balanceAmount} />
              </span>
            )}
          </div>
        </td>

        {/* Status */}
        <td className="p-4 text-center align-middle border-t border-b border-gray-200/60">
          <div className="flex justify-center">
            {renderPaymentStatus(row.status)}
          </div>
        </td>

        {/* Days Elapsed */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          {renderDaysElapsed(row.daysElapsed)}
          {row.dueDate && (
            <div className="text-[10px] text-gray-500 font-medium mt-1">
              Due: {moment(row.dueDate).format("DD-MMM-YYYY")}
            </div>
          )}
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-start justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/payments/receivables/view/${row.receivableId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {CheckEditAction(row, "Receivables", canEdit) ? (
              <Link
                to={`/payments/receivables/edit/${row.receivableId}`}
                state={{ data: row, from: "/payments/receivables", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit receivable"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            ) : null}

            {/* Delete */}
            {CheckDeleteAction(row, canDelete) ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Delete receivable"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this receivable record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// Receivables Card Component
const ReceivablesCard = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("receivables_edit") || GetRole();
  const canDelete = GetAccess("receivables_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/receivable/${row.receivableId}`, setDeleteConfirmOpen);
  };

  const theme = getPaymentStatusTheme(row.status);

  // Card themes classes
  const themeStyles = {
    green: {
      borderLeft: "border-l-4 border-l-emerald-500",
      iconBg: "bg-emerald-50 text-emerald-600",
    },
    blue: {
      borderLeft: "border-l-4 border-l-blue-500",
      iconBg: "bg-blue-50 text-blue-600",
    },
    yellow: {
      borderLeft: "border-l-4 border-l-amber-500",
      iconBg: "bg-amber-50 text-amber-600",
    },
    red: {
      borderLeft: "border-l-4 border-l-rose-500",
      iconBg: "bg-rose-50 text-rose-600",
    }
  };

  const styles = themeStyles[theme] || themeStyles.yellow;
  const borderLeftClass = styles.borderLeft;
  const iconBgClass = styles.iconBg;

  return (
    <div className={`relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-4 flex flex-col overflow-hidden ${borderLeftClass}`}>
      {/* Top Header Row of Card */}
      <div className="flex justify-between items-center gap-4 w-full">
        <div className="flex items-center gap-6 flex-1 min-w-0">
          <div className="flex items-center gap-3 shrink-0">
            {/* Styled Document / Receipt Icon */}
            <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center ${iconBgClass}`}>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Link
                  to={`/payments/receivables/view/${row.receivableId}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="text-gray-800 font-bold hover:underline text-[14px]"
                >
                  {row.invoiceRef || "N/A"}
                </Link>
                <CopyButton text={row.invoiceRef} />
              </div>
              {row.receivedFromName && (
                <span className="text-[11px] text-gray-500 font-semibold mt-0.5">
                  From: {row.receivedFromName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Status Badge */}
        <div className="shrink-0 scale-95 origin-right">
          {renderPaymentStatus(row.status)}
        </div>
      </div>

      {/* Middle Panel Grid */}
      <div className="rounded-xl py-2 flex flex-col sm:flex-row justify-start sm:justify-between sm:items-center gap-1 mt-4 bg-gray-50/50 px-4">
        {/* Category */}
        <div className="flex flex-col gap-1 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Category</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.category || "N/A"}
          </span>
          {row.accountType && (
            <span className="text-[10px] text-gray-400">Acct: {row.accountType}</span>
          )}
        </div>

        {/* Divider */}

        <Divider />
        {/* Due Date & Elapsed */}
        <div className="flex flex-col gap-1 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Timing</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight">
            {row.dueDate ? moment(row.dueDate).format("DD-MMM-YYYY") : "--"}
          </span>
          {row.daysElapsed !== undefined && row.daysElapsed !== null && (
            <span className="text-[10px] text-amber-600 font-semibold">
              {row.daysElapsed} days elapsed
            </span>
          )}
        </div>

        {/* Divider */}
        <Divider />

        {/* Amount Details */}
        <div className="flex flex-col gap-0.5 flex-1 pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Amount</span>
          <span className="text-gray-900 flex items-center gap-1 text-[13px] font-bold leading-none">
            <NuAmount currency={row.vendorCurrency || row.currency} value={row.amount || 0} />
          </span>
          <span className="text-green-600 flex items-center gap-1 font-semibold text-[11px] leading-none mt-1">
            Recvd: <NuAmount currency={row.vendorCurrency || row.currency} value={row.receivedAmount || 0} />
          </span>
          {row.balanceAmount !== undefined && row.balanceAmount !== null && (
            <span className="text-gray-500 text-[10px] font-medium leading-none mt-1">
              Bal: <NuAmount currency={row.vendorCurrency || row.currency} value={row.balanceAmount} />
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-4">
        {/* View Details */}
        <Link
          to={`/payments/receivables/view/${row.receivableId}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
          title="View Details"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="hidden sm:inline">View Details</span>
        </Link>

        {/* Edit & Delete */}
        <div className="flex items-center gap-1.5">
          {CheckEditAction(row, "Receivables", canEdit) ? (
            <Link
              to={`/payments/receivables/edit/${row.receivableId}`}
              state={{ data: row, from: "/payments/receivables", pageIndex, searchQuery }}
              className="border border-sky-100 text-[#0ea5e9] hover:bg-sky-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Edit"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </Link>
          ) : null}

          {CheckDeleteAction(row, canDelete) ? (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Delete"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this receivable record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main ReceivablesTable Component
const ReceivablesTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  listofNumbers = [],
  selectOneFunction,
  selectMultipleFunction,
  fullList = [],
  fetcher,
  dataLoading = false,
  showEdit = false,
  setShowEdit,
  viewMode = "table",
}) => {
  // exact column header names as requested
  const headers = [
    { title: "Invoice Out." },
    { title: "Category" },
    { title: "Amount" },
    { title: "Recvd Amount" },
    { title: "Status", align: "center" },
    { title: "Days Elapsed" },
    { title: "Action", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <ReceivablesTableRow
          row={row}
          isSelected={listofNumbers.includes(row.receivableId)}
          onSelect={() => selectOneFunction(row.receivableId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      renderCard={(row, idx) => (
        <ReceivablesCard
          row={row}
          isSelected={listofNumbers.includes(row.receivableId)}
          onSelect={() => selectOneFunction(row.receivableId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      dataLoading={dataLoading}
      listofNumbers={listofNumbers}
      selectMultipleFunction={selectMultipleFunction}
      fullList={fullList}
      showEdit={showEdit}
      setShowEdit={setShowEdit}
      multiEditLabel="Multi Edit"
    />
  );
};

export default ReceivablesTable;
